import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useAppDispatch } from 'common/hooks/redux'
import { PUSHER_EVENTS } from 'features/Home/constants/infoConstants'
import { procedureApi } from 'features/Procedure/state/procedure.api'
import type { Patient } from 'features/Procedure/state/procedure-api.type'
import { useMultipleChannels } from 'features/Schedule/pusher/MultiplePusherProvider'
import type {
  PatientAppointmentType,
  PatientDeleteEvent,
  PatientStatusUpdateEvent,
  PatientUpdateEvent,
} from 'features/Schedule/pusher/type'
import { useFetchProfileQuery } from 'features/SettingsOld/state/api/settingsApi'

const parsePusherEvent = <T>(event: unknown): T | null => {
  if (event === null || event === undefined) {
    return null
  }

  if (typeof event === 'string') {
    try {
      return JSON.parse(event) as T
    } catch {
      return null
    }
  }

  return event as T
}

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const toUniqueIds = (values: unknown[]): number[] => {
  return Array.from(
    new Set(
      values
        .map((value) => toFiniteNumber(value))
        .filter((value): value is number => value !== null),
    ),
  )
}

const toTimeString = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value
  }

  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.toISOString()
  }

  return null
}

const getPatientAppointmentIds = (patient: Patient): number[] => {
  return toUniqueIds([patient.appointment_id, patient.emr_appointment_id])
}

const patientMatchesAppointmentIds = (patient: Patient, appointmentIds: number[]): boolean => {
  if (!appointmentIds.length) {
    return false
  }

  const patientAppointmentIds = getPatientAppointmentIds(patient)
  return patientAppointmentIds.some((id) => appointmentIds.includes(id))
}

type UseProcedurePatientStatusPusherOptions = {
  selectedPatientId?: number | null
  onSelectedPatientEvent?: () => void
}

const PUSHER_REFRESH_DEBOUNCE_MS = 1000

export const useProcedurePatientStatusPusher = (
  siteId?: string,
  options?: UseProcedurePatientStatusPusherOptions,
) => {
  const dispatch = useAppDispatch()
  const { channels, bookingIds, setBookingIds } = useMultipleChannels()
  const selectedPatientId = options?.selectedPatientId ?? null
  const onSelectedPatientEvent = options?.onSelectedPatientEvent
  const isSilentRefreshInFlightRef = useRef(false)
  const pendingSilentRefreshRef = useRef(false)
  const runSilentRefreshRef = useRef<() => void>(() => {})
  const selectedPatientIdRef = useRef<number | null>(selectedPatientId)
  const patientRefreshDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const selectedPatientRefreshDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const queuedSelectedPatientRefreshIdRef = useRef<number | null>(null)

  const { data: profile } = useFetchProfileQuery(undefined, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  })

  const doctorsUsers = useMemo(
    () => profile?.users?.filter((user) => user.is_doctor || user.is_provider) ?? [],
    [profile?.users],
  )

  const doctorsIds = useMemo(() => {
    return doctorsUsers.map((staff) => String(staff.id))
  }, [doctorsUsers])

  useEffect(() => {
    selectedPatientIdRef.current = selectedPatientId
  }, [selectedPatientId])

  useEffect(() => {
    if (doctorsIds.length > 0) {
      setBookingIds(doctorsIds)
    }
  }, [JSON.stringify(doctorsIds), setBookingIds])

  const runSilentRefresh = useCallback(() => {
    if (!siteId) {
      return
    }

    isSilentRefreshInFlightRef.current = true

    const query = dispatch(
      procedureApi.endpoints.fetchEncounterPatients.initiate(
        { siteId },
        { forceRefetch: true, subscribe: false },
      ),
    )

    void query
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        isSilentRefreshInFlightRef.current = false

        if (pendingSilentRefreshRef.current) {
          pendingSilentRefreshRef.current = false
          runSilentRefreshRef.current()
        }
      })
  }, [dispatch, siteId])

  useEffect(() => {
    runSilentRefreshRef.current = runSilentRefresh
  }, [runSilentRefresh])

  const requestSilentRefresh = useCallback(() => {
    if (!siteId) {
      return
    }

    if (isSilentRefreshInFlightRef.current) {
      pendingSilentRefreshRef.current = true
      return
    }

    runSilentRefresh()
  }, [runSilentRefresh, siteId])

  const schedulePatientListRefresh = useCallback(() => {
    if (!siteId) {
      return
    }

    if (patientRefreshDebounceTimeoutRef.current) {
      clearTimeout(patientRefreshDebounceTimeoutRef.current)
    }

    patientRefreshDebounceTimeoutRef.current = setTimeout(() => {
      patientRefreshDebounceTimeoutRef.current = null
      requestSilentRefresh()
    }, PUSHER_REFRESH_DEBOUNCE_MS)
  }, [requestSilentRefresh, siteId])

  const scheduleSelectedPatientEncounterRefreshIfAffected = useCallback(
    (affectedPatientId: number | null) => {
      if (!onSelectedPatientEvent || affectedPatientId === null) {
        return
      }

      if (selectedPatientIdRef.current !== affectedPatientId) {
        return
      }

      queuedSelectedPatientRefreshIdRef.current = affectedPatientId

      if (selectedPatientRefreshDebounceTimeoutRef.current) {
        clearTimeout(selectedPatientRefreshDebounceTimeoutRef.current)
      }

      selectedPatientRefreshDebounceTimeoutRef.current = setTimeout(() => {
        selectedPatientRefreshDebounceTimeoutRef.current = null

        const queuedPatientId = queuedSelectedPatientRefreshIdRef.current
        queuedSelectedPatientRefreshIdRef.current = null

        if (queuedPatientId !== null && selectedPatientIdRef.current === queuedPatientId) {
          onSelectedPatientEvent()
        }
      }, PUSHER_REFRESH_DEBOUNCE_MS)
    },
    [onSelectedPatientEvent],
  )

  const patientUpdate = useCallback(
    (rawEvent: PatientUpdateEvent | string) => {
      const parsedEvent = parsePusherEvent<PatientUpdateEvent>(rawEvent)
      if (!parsedEvent) {
        return
      }

      const eventData = parsePusherEvent<PatientAppointmentType>(parsedEvent.data)
      if (!siteId || !parsedEvent.shouldUpdate || !eventData) {
        return
      }

      const appointmentIds = toUniqueIds([eventData.id, eventData.emr_appointment_id])
      const payloadPatientId = toFiniteNumber(eventData.patient?.id ?? null)
      let matchedPatientId: number | null = null
      let hasMatchedPatient = false

      dispatch(
        procedureApi.util.updateQueryData('fetchEncounterPatients', { siteId }, (draft) => {
          const matchedPatient = draft.patients.find(({ patient }) =>
            patientMatchesAppointmentIds(patient, appointmentIds),
          )

          if (!matchedPatient) {
            return
          }

          hasMatchedPatient = true
          matchedPatientId = toFiniteNumber(matchedPatient.patient.patient_id ?? null)

          const statusCode = toFiniteNumber(eventData.status?.code ?? null)
          const resolvedStatus = draft.patient_statuses.find((status) => status.code === statusCode)
          const appointmentStartTime = toTimeString(eventData.appointment_start_time)
          const startTime = toTimeString(eventData.start_time)
          const appointmentEndTime = toTimeString(eventData.appointment_end_time)
          const endTime = toTimeString(eventData.end_time)
          const currentStartTime = startTime ?? appointmentStartTime
          const currentEndTime = endTime ?? appointmentEndTime

          matchedPatient.patient.patient_id =
            eventData.patient?.id ?? matchedPatient.patient.patient_id
          matchedPatient.patient.first_name =
            eventData.patient?.first_name ?? matchedPatient.patient.first_name
          matchedPatient.patient.last_name =
            eventData.patient?.last_name ?? matchedPatient.patient.last_name
          matchedPatient.patient.phone = eventData.patient?.phone ?? matchedPatient.patient.phone
          matchedPatient.patient.appointment_id =
            toFiniteNumber(eventData.id) ?? matchedPatient.patient.appointment_id ?? null
          matchedPatient.patient.emr_appointment_id =
            toFiniteNumber(eventData.emr_appointment_id) ??
            matchedPatient.patient.emr_appointment_id ??
            null
          matchedPatient.patient.appointment_start_time =
            currentStartTime ?? matchedPatient.patient.appointment_start_time ?? null
          matchedPatient.patient.appointment_time =
            currentStartTime ?? matchedPatient.patient.appointment_time ?? null
          matchedPatient.patient.start_time = currentStartTime ?? matchedPatient.patient.start_time
          matchedPatient.patient.appointment_time_interval =
            currentStartTime && currentEndTime
              ? `${currentStartTime}-${currentEndTime}`
              : matchedPatient.patient.appointment_time_interval ?? null

          if (statusCode !== null) {
            matchedPatient.patient.status_code = statusCode
            matchedPatient.patient.status = {
              code: statusCode,
              name:
                eventData.status?.name ??
                resolvedStatus?.name ??
                matchedPatient.patient.status?.name ??
                '',
            }
          }
        }) as any,
      )

      if (!hasMatchedPatient) {
        schedulePatientListRefresh()
      }

      scheduleSelectedPatientEncounterRefreshIfAffected(payloadPatientId ?? matchedPatientId)
    },
    [
      dispatch,
      schedulePatientListRefresh,
      scheduleSelectedPatientEncounterRefreshIfAffected,
      siteId,
    ],
  )

  const patientCreate = useCallback(
    (rawEvent: PatientUpdateEvent | string) => {
      const parsedEvent = parsePusherEvent<PatientUpdateEvent>(rawEvent)
      if (!siteId || !parsedEvent?.shouldUpdate) {
        return
      }

      const eventData = parsePusherEvent<PatientAppointmentType>(parsedEvent.data)
      const payloadPatientId = toFiniteNumber(eventData?.patient?.id ?? null)
      scheduleSelectedPatientEncounterRefreshIfAffected(payloadPatientId)

      schedulePatientListRefresh()
    },
    [schedulePatientListRefresh, scheduleSelectedPatientEncounterRefreshIfAffected, siteId],
  )

  const patientDelete = useCallback(
    (rawEvent: PatientDeleteEvent | string) => {
      const parsedEvent = parsePusherEvent<PatientDeleteEvent>(rawEvent)
      if (!siteId || !parsedEvent?.shouldUpdate) {
        return
      }

      const appointmentIds = toUniqueIds([parsedEvent.appointment_id])
      if (!appointmentIds.length) {
        return
      }

      dispatch(
        procedureApi.util.updateQueryData('fetchEncounterPatients', { siteId }, (draft) => {
          draft.patients = draft.patients.filter(
            ({ patient }) => !patientMatchesAppointmentIds(patient, appointmentIds),
          )
        }) as any,
      )
    },
    [dispatch, siteId],
  )

  const patientStatusUpdate = useCallback(
    (rawEvent: PatientStatusUpdateEvent | string) => {
      const event = parsePusherEvent<PatientStatusUpdateEvent>(rawEvent)
      if (!event) {
        return
      }

      const { appointment_id, shouldUpdate, status_code, status_text } = event

      if (!siteId || !shouldUpdate) {
        return
      }

      let hasMatchedPatient = false

      const appointmentIds = toUniqueIds([appointment_id])

      dispatch(
        procedureApi.util.updateQueryData('fetchEncounterPatients', { siteId }, (draft) => {
          const matchedPatient = draft.patients.find(({ patient }) => {
            return patientMatchesAppointmentIds(patient, appointmentIds)
          })

          if (!matchedPatient) {
            return
          }

          hasMatchedPatient = true

          matchedPatient.patient.status_code = status_code

          const resolvedStatus = draft.patient_statuses.find(
            (status) => status.code === status_code,
          )

          matchedPatient.patient.status = {
            code: status_code,
            name: resolvedStatus?.name ?? status_text ?? matchedPatient.patient.status?.name ?? '',
          }
        }) as any,
      )

      if (!hasMatchedPatient) {
        schedulePatientListRefresh()
      }
    },
    [dispatch, schedulePatientListRefresh, siteId],
  )

  useEffect(() => {
    return () => {
      if (patientRefreshDebounceTimeoutRef.current) {
        clearTimeout(patientRefreshDebounceTimeoutRef.current)
      }

      if (selectedPatientRefreshDebounceTimeoutRef.current) {
        clearTimeout(selectedPatientRefreshDebounceTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!siteId || !channels.size || !bookingIds.length) {
      return
    }

    channels.forEach((channel) => {
      channel.bind(PUSHER_EVENTS.PATIENT_UPDATED, patientUpdate)
      channel.bind(PUSHER_EVENTS.PATIENT_CREATE, patientCreate)
      channel.bind(PUSHER_EVENTS.PATIENT_DELETE, patientDelete)
      channel.bind(PUSHER_EVENTS.PATIENT_STATUS_UPDATE, patientStatusUpdate)
    })

    return () => {
      channels.forEach((channel) => {
        channel.unbind(PUSHER_EVENTS.PATIENT_UPDATED, patientUpdate)
        channel.unbind(PUSHER_EVENTS.PATIENT_CREATE, patientCreate)
        channel.unbind(PUSHER_EVENTS.PATIENT_DELETE, patientDelete)
        channel.unbind(PUSHER_EVENTS.PATIENT_STATUS_UPDATE, patientStatusUpdate)
      })
    }
  }, [
    siteId,
    Array.from(channels.keys()).join(','),
    bookingIds.join(','),
    patientUpdate,
    patientCreate,
    patientDelete,
    patientStatusUpdate,
  ])
}
